import {Nullable} from '../../../app/TypeHelpers'
import {BusinessSchedule, OptionSetting, OvernightFacilitySetting} from '../../facilities/FacilityForReservation'
import {BusinessScheduleWithDateTime} from '../ReserveFacilityPaymentModel'
import {ReservationOption} from '../ReserveFacilityModel'
import {ReservePaymentCalculatorOvernight} from './ReservePaymentCalculatorOvernight'
import {ReserveOptionPaymentCalculator} from './ReserveOptionPaymentCalculator'
import ReservePaymentCalculator from './ReservePaymentCalculator'
import {ReservePaymentCalculatorDurationStraddle} from './ReservePaymentCalculatorDurationStraddle'
import {convertToNullableMoment} from '../helpers/helpers'
import {ReservePaymentCalculatorDurationNormal} from './ReservePaymentCalculatorDurationNormal'
import {ReservePaymentCalculatorDuration} from './ReservePaymentCalculatorDuration'

export default class ReservePaymentCalculatorFactory {
    private stayAvailability: Nullable<boolean> = null
    private overnightFacilityData: Nullable<OvernightFacilitySetting> = null

    private facilityOptionSettings: Nullable<OptionSetting[]> = null

    private isStraddleDurationFacility: Nullable<boolean> = null
    private businessSchedulesForNormal: Nullable<BusinessScheduleWithDateTime[]> = null
    private businessSchedulesForStraddle: Nullable<BusinessSchedule[]> = null

    private reservationOptions: Nullable<ReservationOption[]> = null

    private startDate: Nullable<string> = null
    private startTime: Nullable<string> = null
    private endDate: Nullable<string> = null
    private endTime: Nullable<string> = null
    private stayCount: Nullable<number> = null

    reset(): ReservePaymentCalculatorFactory {
        this.stayAvailability = null
        this.overnightFacilityData = null
        this.facilityOptionSettings = null
        this.isStraddleDurationFacility = null
        this.businessSchedulesForNormal = null
        this.businessSchedulesForStraddle = null
        this.reservationOptions = null
        this.startDate = null
        this.startTime = null
        this.endDate = null
        this.endTime = null
        this.stayCount = null

        return this
    }

    withStayAvailable(stayAvailability: boolean): void {
        this.stayAvailability = stayAvailability
    }

    withOvernightFacilityData(
        overnightFacilityData: Nullable<OvernightFacilitySetting>,
    ): void {
        this.overnightFacilityData = overnightFacilityData
    }

    withFacilityOptionSettings(facilityOptionSettings: OptionSetting[]): void {
        this.facilityOptionSettings = facilityOptionSettings
    }

    withReservationOptions(options: ReservationOption[]): void {
        this.reservationOptions = options
    }

    withDurationReservationData(
        startDate: string,
        startTime: Nullable<string>,
        endDate: Nullable<string>,
        endTime: Nullable<string>,
    ): void {
        this.startDate = startDate
        this.startTime = startTime
        this.endDate = endDate
        this.endTime = endTime
    }

    withBusinessScheduleForStraddle(
        businessSchedulesForStraddle: Nullable<BusinessSchedule[]>,
    ): void {
        this.isStraddleDurationFacility = businessSchedulesForStraddle ? true : null
        this.businessSchedulesForStraddle = businessSchedulesForStraddle
    }

    withBusinessSchedulesDependOnTargetDate(
        businessSchedulesForNormal: Nullable<BusinessScheduleWithDateTime[]>,
    ): void {
        this.isStraddleDurationFacility = businessSchedulesForNormal ? false : null
        this.businessSchedulesForNormal = businessSchedulesForNormal
    }

    withOvernightReservationData(startDate: Nullable<string>, stayCount: number): void {
        this.startDate = startDate
        this.stayCount = stayCount
    }

    create(): Nullable<ReservePaymentCalculator> {
        if (this.stayAvailability === null) {
            return null
        }

        if (this.stayAvailability) {
            return this.createOvernightCalculator()
        } else {
            return this.createDurationCalculator()
        }
    }

    private createOvernightCalculator(): ReservePaymentCalculatorOvernight {
        return new ReservePaymentCalculatorOvernight(
            this.overnightFacilityData,
            this.startDate,
            this.stayCount,
            this.createOptionCalculator(),
        )
    }

    private createDurationCalculator(): Nullable<ReservePaymentCalculatorDuration> {
        if (this.isStraddleDurationFacility === null) {
            return null
        }

        if (this.isStraddleDurationFacility) {
            return new ReservePaymentCalculatorDurationStraddle(
                this.businessSchedulesForStraddle || [],
                convertToNullableMoment(this.startDate, this.startTime),
                convertToNullableMoment(this.endDate, this.endTime),
                this.createOptionCalculator(),
            )
        } else {
            return new ReservePaymentCalculatorDurationNormal(
                this.businessSchedulesForNormal || [],
                convertToNullableMoment(this.startDate, this.startTime),
                convertToNullableMoment(this.endDate, this.endTime),
                this.createOptionCalculator(),
            )
        }
    }

    private createOptionCalculator(): ReserveOptionPaymentCalculator {
        return new ReserveOptionPaymentCalculator(
            this.facilityOptionSettings,
            this.reservationOptions,
            this.startDate,
            this.startTime,
            this.endDate,
            this.endTime,
        )
    }
}
